import { useState, useEffect, useCallback } from 'react';
import {
  FlaskConical,
  Play,
  Trash2,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Terminal,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Tag,
  ExternalLink,
  Copy,
  Eye,
  HelpCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  fetchManifest,
  cleanTestData,
  loadChecklistState,
  saveChecklistState,
  clearChecklistState,
  type ScenarioManifestEntry,
} from '../../services/testHarness';

type Target = 'emulator' | 'production';

interface GroupedScenarios {
  [iterScope: string]: ScenarioManifestEntry[];
}

export default function TestHarnessPage() {
  const [scenarios, setScenarios] = useState<ScenarioManifestEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<Target>('emulator');
  const [expandedScenarios, setExpandedScenarios] = useState<Set<string>>(new Set());
  const [checklistState, setChecklistState] = useState<Record<string, boolean[]>>({});
  const [seedingScenario, setSeedingScenario] = useState<string | null>(null);
  const [cleaning, setCleaning] = useState(false);
  const [showProductionConfirm, setShowProductionConfirm] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    loadManifest();
    setChecklistState(loadChecklistState());
  }, []);

  const loadManifest = async () => {
    try {
      setLoading(true);
      setError(null);
      const manifest = await fetchManifest();
      setScenarios(manifest);
    } catch (err) {
      setError('Failed to load scenario manifest. Make sure manifest.json exists.');
    } finally {
      setLoading(false);
    }
  };

  const grouped: GroupedScenarios = scenarios.reduce<GroupedScenarios>((acc, scenario) => {
    const scope = scenario.iterScope;
    if (!acc[scope]) acc[scope] = [];
    acc[scope].push(scenario);
    return acc;
  }, {});

  const toggleExpanded = (path: string) => {
    setExpandedScenarios((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const handleCheckToggle = useCallback(
    (scenarioPath: string, index: number) => {
      setChecklistState((prev) => {
        const scenario = scenarios.find((s) => s.path === scenarioPath);
        if (!scenario) return prev;

        const current = prev[scenarioPath] || new Array(scenario.checklist.length).fill(false);
        const updated = [...current];
        updated[index] = !updated[index];

        const next = { ...prev, [scenarioPath]: updated };
        saveChecklistState(next);
        return next;
      });
    },
    [scenarios]
  );

  const handleClearChecklist = (scenarioPath: string) => {
    clearChecklistState(scenarioPath);
    setChecklistState((prev) => {
      const next = { ...prev };
      delete next[scenarioPath];
      return next;
    });
    toast.success('Checklist cleared');
  };

  const handleSeed = async (scenario: ScenarioManifestEntry) => {
    if (target === 'production') {
      toast.error('Production seeding from web UI is not yet supported. Use the CLI with --target=production');
      return;
    }

    const command = `cd testing; npm run seed -- ${scenario.path}`;
    navigator.clipboard.writeText(command);
    setSeedingScenario(scenario.path);
    toast.success('Seed command copied! Paste in PowerShell.', { duration: 4000 });
    setTimeout(() => setSeedingScenario(null), 1000);
  };

  const handleClean = async () => {
    if (target === 'production') {
      setShowProductionConfirm(true);
      return;
    }

    setCleaning(true);
    try {
      const result = await cleanTestData();
      toast.success(`Cleaned ${result.deletedCount} test documents`);
    } catch (err) {
      toast.error('Failed to clean test data');
    } finally {
      setCleaning(false);
    }
  };

  const handleTargetChange = (newTarget: Target) => {
    if (newTarget === 'production') {
      setShowProductionConfirm(true);
    } else {
      setTarget(newTarget);
    }
  };

  const confirmProductionTarget = () => {
    setTarget('production');
    setShowProductionConfirm(false);
  };

  const getChecklistProgress = (scenarioPath: string, total: number) => {
    const checks = checklistState[scenarioPath] || [];
    const completed = checks.filter(Boolean).length;
    return { completed, total };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FlaskConical className="w-7 h-7 text-purple-400" />
          <h1 className="text-2xl font-bold text-white">Test Harness</h1>
        </div>
        <div className="flex items-center gap-3">
          {/* Target Toggle */}
          <div className="flex rounded-lg overflow-hidden border border-gray-700">
            <button
              onClick={() => handleTargetChange('emulator')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                target === 'emulator'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              Emulator
            </button>
            <button
              onClick={() => handleTargetChange('production')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                target === 'production'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              Production
            </button>
          </div>

          {/* Clean Button */}
          <button
            onClick={handleClean}
            disabled={cleaning}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-400 border border-red-800 rounded-lg hover:bg-red-900/30 transition-colors disabled:opacity-50"
          >
            {cleaning ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            Clear Test Data
          </button>
        </div>
      </div>

      {/* How-To Guide Toggle */}
      <button
        onClick={() => setShowGuide((prev) => !prev)}
        className="flex items-center gap-2 mb-4 text-sm text-gray-400 hover:text-white transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
        {showGuide ? 'Hide' : 'Show'} testing guide
      </button>

      {showGuide && (
        <div className="mb-6 p-4 rounded-lg bg-gray-800/70 border border-gray-700 text-sm text-gray-300 space-y-3">
          <h3 className="font-semibold text-white">How to run a scenario</h3>
          <ol className="list-decimal list-inside space-y-1.5">
            <li>Start the Firebase emulator: <code className="text-purple-300">firebase emulators:start</code></li>
            <li>Click <span className="text-purple-300 font-medium">Seed</span> on a scenario below — the command is copied to your clipboard</li>
            <li>Paste into PowerShell and run it</li>
            <li>Click <span className="text-purple-300 font-medium">Open Event</span> to jump to the attendee page, or use the DJ link</li>
            <li>Follow the checklist and tick off each item as you verify</li>
            <li>When done, click <span className="text-red-400 font-medium">Clear Test Data</span> above to clean up</li>
          </ol>
          <div className="pt-2 border-t border-gray-700">
            <span className="text-gray-500">DJ test login:</span>{' '}
            <code className="text-purple-300">testdj@votebeats.test</code> /{' '}
            <code className="text-purple-300">testpassword123</code>
          </div>
        </div>
      )}

      {/* Target Warning */}
      {target === 'production' && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-900/30 border border-red-800 text-red-300 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>Production mode: operations affect deployed Firestore. Only __test__ prefixed documents will be created/deleted.</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 p-4 mb-4 rounded-lg bg-red-900/30 border border-red-800 text-red-300">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={loadManifest} className="ml-auto text-sm underline hover:text-white">
            Retry
          </button>
        </div>
      )}

      {/* Scenario Groups */}
      {Object.entries(grouped).map(([scope, scopeScenarios]) => (
        <div key={scope} className="mb-6">
          <h2 className="text-lg font-semibold text-gray-300 mb-3 flex items-center gap-2">
            <Tag className="w-4 h-4 text-purple-400" />
            {scope}
            <span className="text-sm font-normal text-gray-500">
              ({scopeScenarios.length} scenario{scopeScenarios.length !== 1 ? 's' : ''})
            </span>
          </h2>

          <div className="space-y-2">
            {scopeScenarios.map((scenario) => {
              const isExpanded = expandedScenarios.has(scenario.path);
              const isSeeding = seedingScenario === scenario.path;
              const progress = getChecklistProgress(scenario.path, scenario.checklist.length);
              const allPassed = progress.completed === progress.total && progress.total > 0;

              return (
                <div
                  key={scenario.path}
                  className="rounded-lg border border-gray-700 bg-gray-800/50 overflow-hidden"
                >
                  {/* Scenario Header */}
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => toggleExpanded(scenario.path)}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium truncate">{scenario.name}</span>
                        {allPassed && (
                          <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                        )}
                      </div>
                      {scenario.part && (
                        <span className="text-xs text-gray-500">{scenario.part}</span>
                      )}
                    </div>

                    {/* Progress */}
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      {progress.completed}/{progress.total}
                    </span>

                    {/* Tags */}
                    <div className="hidden sm:flex gap-1 flex-shrink-0">
                      {scenario.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 text-xs rounded bg-gray-700 text-gray-400"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Seed Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSeed(scenario);
                      }}
                      disabled={isSeeding}
                      className="flex items-center gap-1.5 px-3 py-1 text-sm font-medium text-purple-300 border border-purple-800 rounded-md hover:bg-purple-900/30 transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                      {isSeeding ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Play className="w-3.5 h-3.5" />
                      )}
                      Seed
                    </button>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-gray-700 p-4">
                      {/* Purpose */}
                      <p className="text-sm text-gray-400 mb-4">{scenario.purpose}</p>

                      {/* Quick Links */}
                      {scenario.eventUrl && (
                        <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-lg bg-gray-900/50 border border-gray-700">
                          <span className="text-xs text-gray-500 mr-1">Open:</span>
                          <a
                            href={`/e/${scenario.eventUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-green-300 border border-green-800 rounded hover:bg-green-900/30 transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Attendee View
                          </a>
                          <a
                            href={`/events/__test__event-test-001/manage/queue`}
                            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-300 border border-blue-800 rounded hover:bg-blue-900/30 transition-colors"
                          >
                            <Eye className="w-3 h-3" />
                            DJ Queue
                          </a>
                          <a
                            href={`/events/__test__event-test-001/manage/pending`}
                            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-yellow-300 border border-yellow-800 rounded hover:bg-yellow-900/30 transition-colors"
                          >
                            <Eye className="w-3 h-3" />
                            DJ Pending
                          </a>
                          {scenario.djLogin && (
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(scenario.djLogin!.email);
                                toast.success('DJ email copied');
                              }}
                              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-400 border border-gray-700 rounded hover:bg-gray-700 transition-colors ml-auto"
                            >
                              <Copy className="w-3 h-3" />
                              {scenario.djLogin.email}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Prerequisites */}
                      {scenario.prerequisites && (
                        <div className="text-sm text-yellow-400 mb-3">
                          Prerequisite: {scenario.prerequisites}
                        </div>
                      )}

                      {/* Verification Checklist */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-semibold text-gray-300">Verification Checklist</h3>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleClearChecklist(scenario.path)}
                              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                            >
                              <RotateCcw className="w-3 h-3" />
                              Reset
                            </button>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          {scenario.checklist.map((check, idx) => {
                            const checks = checklistState[scenario.path] || [];
                            const isChecked = checks[idx] || false;

                            return (
                              <label
                                key={idx}
                                className="flex items-start gap-2 cursor-pointer group"
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleCheckToggle(scenario.path, idx)}
                                  className="mt-0.5 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                                />
                                <span
                                  className={`text-sm ${
                                    isChecked ? 'text-gray-500 line-through' : 'text-gray-300'
                                  } group-hover:text-white transition-colors`}
                                >
                                  {check}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      {/* Claude Code Commands */}
                      <div className="flex gap-2 pt-3 border-t border-gray-700">
                        <button
                          onClick={() => {
                            const scope = scenario.iterScope;
                            navigator.clipboard.writeText(`/new-scenario ${scope}`);
                            toast.success('Copied /new-scenario command');
                          }}
                          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-400 border border-gray-700 rounded hover:bg-gray-700 transition-colors"
                        >
                          <Terminal className="w-3 h-3" />
                          New Scenario
                        </button>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`/edit-scenario ${scenario.path}`);
                            toast.success('Copied /edit-scenario command');
                          }}
                          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-400 border border-gray-700 rounded hover:bg-gray-700 transition-colors"
                        >
                          <Terminal className="w-3 h-3" />
                          Edit Scenario
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Empty State */}
      {scenarios.length === 0 && !error && (
        <div className="text-center py-12 text-gray-500">
          <FlaskConical className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No test scenarios found.</p>
          <p className="text-sm mt-1">Run <code className="text-purple-400">/new-scenario</code> in Claude Code to create one.</p>
        </div>
      )}

      {/* Production Confirmation Modal */}
      {showProductionConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center gap-2 text-red-400 mb-3">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="font-semibold text-lg">Switch to Production?</h3>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Operations will target the deployed Firestore database. Only documents
              with the <code className="text-red-300">__test__</code> prefix will be created or deleted.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowProductionConfirm(false)}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmProductionTarget}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-500 transition-colors"
              >
                Switch to Production
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
